import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {COHORT_METRIC_ENTRY_DEFAULT_PROPS} from 'common/report/util/cohorts/transformations/entries';
import {currentProjectStore} from 'common/report/stores/project-details';
import {useStore} from 'common/util/component-helpers';
import {emitTrackEvent} from 'common/util/dom';

import {Cohort} from '../cohorts-query-builder/models/cohort';
import {TrackingEvent} from '../cohorts-query-builder/util/tracking';
import {SerializedCohort} from '../cohorts-query-builder/models/types/serialized-cohort-types';
import {QueryBuilderUpdateEvent} from '../query-builder/types';
import {CohortGroupEntry, RowTypeComplexOption, SelectorTypeDefinitions} from '../query-entry-section/types';
import {EventItem} from '../query-block/events-menu/types';
import {StagedCohort} from '../cohorts-query-builder/models/builder/staged-cohort';
import {SerializedStagedCohort} from '../cohorts-query-builder/models/types/serialized-staged-cohort-types';
import {template} from './template';
import css from './index.styl?inline';

interface CohortsQueryBuilderJsxProps {
  attrs: {
    cohort: SerializedCohort;
    'staged-cohort': SerializedStagedCohort;
    'project-id': number;
    'project-has-cohorts': boolean;
    'data-group-id': string;
    'support-b2b-cohorts': boolean;
    disabled: boolean;
    'track-name': string;
    'user-id': number;
    'workspace-id': number;
    'project-as-of-date': string;
    'max-data-history-days': string;
    'project-has-people': boolean;
    'hide-reset-button': boolean;
    'non-collapsible': boolean;
    'hide-edit-controls': boolean;
    'query-source': string;
    'header-title': string;
  };
  props?: EmptyObj;
}

type CohortsQueryBuilderState = EmptyObj;

export class CohortsQueryBuilder extends Component<CohortsQueryBuilderState, CohortsQueryBuilderJsxProps['attrs']> {
  static get attrsSchema(): AttrsSchema<CohortsQueryBuilderJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      cohort: {
        type: `json`,
        default: null,
        description: `the serialized cohort you want this builder to render`,
      },
      /** @todo [pablo] drop this to remove backwards compatibility with site calls using staged-cohort */
      'staged-cohort': {
        type: `json`,
        default: null,
        description: `the serialized cohort you want this builder to render`,
      },
      'project-id': {type: `number`},
      'project-has-cohorts': `boolean`,
      'header-title': `string`,
      'hide-reset-button': `boolean`,
      'hide-edit-controls': `boolean`,
      'data-group-id': {
        type: `string`,
        default: null,
        description: `the data group id that this cohort-builder queries with`,
      },
      'support-b2b-cohorts': `boolean`,
      disabled: {type: `boolean`, description: `whether this builder can be edited`},
      'track-name': `string`,
      'user-id': {type: `number`, description: `ID of user viewing the builder`},
      'workspace-id': {type: `number`},
      'project-has-people': `boolean`,
      'max-data-history-days': {
        type: `number`,
        description: `max days time menu can query into data history`,
      },
      'query-source': `string`,
      'non-collapsible': `boolean`,
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {},
      helpers: {},
    };
  }

  connectedCallback() {
    super.connectedCallback();

    useStore(this, currentProjectStore);
  }

  hasNewQBEnabled() {
    const groups = Array.isArray(this.cohort.groups) && this.cohort.groups;

    // handle the case where we attempted to convert a cohort but the conversion failed
    if (groups?.length === 0) {
      return false;
    }

    return this.attr(`staged-cohort`) === null && groups?.length > 0;
  }

  hasPeopleEnabled() {
    return currentProjectStore.value?.hasIntegratedEngage;
  }

  getProjectAsOfDate() {
    return currentProjectStore.value?.projectAsOfDate ?? ``;
  }

  get cohort() {
    const defaultCohort = Cohort.createCohortFromStaged(StagedCohort.deserialize(null)).serialize();
    return Cohort.parseCohort(this.attr(`cohort`) ?? defaultCohort);
  }

  get rowTypes(): RowTypeComplexOption[] {
    return [
      {
        label: COHORT_METRIC_ENTRY_DEFAULT_PROPS.event.label,
        value: SelectorTypeDefinitions.CohortGroup,
        readOnlyEvent: COHORT_METRIC_ENTRY_DEFAULT_PROPS.event as EventItem,
      },
    ];
  }

  /**
   * Updates the selected cohort with the updated groups from the qb
   */
  handleQueryUpdate(ev: QueryBuilderUpdateEvent) {
    const cohort = this.cohort;
    cohort.groups = ev.detail.entries as CohortGroupEntry[];

    // prevent an empty cohort definition from being constructed
    if (cohort.groups.length === 0) {
      cohort.groups = [COHORT_METRIC_ENTRY_DEFAULT_PROPS];
    }

    const detail = {
      cohort,
      queryReason: ev.detail.queryReason,
    };

    this.dispatchEvent(new CustomEvent(`cohortUpdated`, {detail}));
    this.trackUpdate(cohort);
  }

  trackUpdate(cohort: Cohort) {
    emitTrackEvent(this, {
      eventName: TrackingEvent.UpdateCohort,
      eventPrefix: `Cohorts`,
      props: {
        ...cohort.toTrackingData(),
        // Track Query Source for sessions
        ...(this.attr(`query-source`)
          ? {
              'Query Source': this.attr(`query-source`),
            }
          : {}),
      },
    });
  }
}

export const CohortsLiteralBuilderEl = defineAndMakeJsxFactory(`mp-cohorts-query-builder`, CohortsQueryBuilder);
